import { useParams } from 'react-router-dom'
import { businessPath } from 'utils/businessRoutes'
import { browserDate, browserTimeZone } from 'utils/time'
import ImageRecommendationSession from '../OfferDetails/components/ImageUpload/ImageRecommendationSession';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { keyframes } from '@emotion/react';
import {
  Alert,
  Box,
  Button,
  Chip,
  ClickAwayListener,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import AddCommentRoundedIcon from '@mui/icons-material/AddCommentRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import api from 'utils/api';
import KaiyleeOfferActions from './KaiyleeOfferActions';
import PaymentMethodDialog from './PaymentMethodDialog';
import KaiyleeContactDialog from './KaiyleeContactDialog';
import ServiceCapacityNotice from 'components/ServiceCapacityNotice';
import { isServicePaused, KAIYLEE_BUSY, KAIYLEE_BUSY_MESSAGE, observeServiceAvailability, useServiceCooldown } from 'utils/serviceAvailability';
import StableMultilineTextField from './StableMultilineTextField';

const PANEL_WIDTH = 420;
const HEADER_OFFSET_PX = 72; // BusinessHeader height (approx); keeps panel from covering header

const typingDotPulse = keyframes`
  0%, 60%, 100% {
    opacity: 0.38;
    transform: translateY(0) scale(0.9);
  }
  30% {
    opacity: 1;
    transform: translateY(-3px) scale(1);
  }
`;

function uuid() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function nowIso() {
  return new Date().toISOString();
}

function buildChatStartPayload(businessId) {
  if (businessId == null) {
    throw new Error('A current business is required to start Kaiylee.');
  }
  return { businessId, timeZone: browserTimeZone() };
}

function normalizeSessionList(response) {
  if (Array.isArray(response)) return response;
  return Array.isArray(response?.content) ? response.content : [];
}

function sessionStatusLabel(session) {
  if (session?.conversationState === 'ENDED') return 'Ended';
  if (session?.complete) return 'Complete';
  if (session?.readyForReview) return 'Ready for review';
  return 'In progress';
}

function formatSessionTimestamp(value) {
  if (!value) return 'Recently updated';
  const date = browserDate(value);
  return Number.isNaN(date.getTime()) ? 'Recently updated' : date.toLocaleString();
}

function isAllowedExpirationReply(reply, startDate) {
  const normalized = String(reply || '').trim().toLocaleLowerCase();
  if (normalized === 'no end date') return false;

  const relativeYears = normalized.match(/^in\s+(\d+)\s+years?$/);
  if (relativeYears && Number(relativeYears[1]) > 1) return false;

  const absoluteDate = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!absoluteDate || !startDate) return true;

  const start = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${absoluteDate[0]}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return true;

  const latestEnd = new Date(start);
  latestEnd.setUTCFullYear(latestEnd.getUTCFullYear() + 1);
  return end <= latestEnd;
}

function normalizeList(value) {
  const values = Array.isArray(value)
    ? value
    : String(value || '')
    .split(',')
  const unique = new Map();
  values
    .map((x) => String(x).trim())
    .filter(Boolean)
    .forEach((item) => unique.set(item.toLocaleLowerCase(), item));
  return [...unique.values()];
}

function numberOrUndefined(value) {
  if (value === null || value === undefined || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function normalizePeriod(value) {
  return value ? String(value).toUpperCase() : undefined;
}

function periodLabel(value) {
  const normalized = String(value || '').toUpperCase();
  if (normalized === '1' || normalized === 'DAY' || normalized === 'DAILY') return 'daily';
  if (normalized === '2' || normalized === 'WEEK' || normalized === 'WEEKLY') return 'weekly';
  if (normalized === '3' || normalized === 'TOTAL' || normalized === 'CAMPAIGN') return 'total campaign';
  return normalized ? normalized.toLowerCase() : '';
}

function formatBudget(value, period) {
  if (value == null) return '';
  const label = periodLabel(period);
  return `$${Number(value).toLocaleString()}${label ? ` ${label}` : ''}`;
}

function formatClaims(value, period, unlimitedClaims = false) {
  if (unlimitedClaims) return 'No limit';
  if (value == null) return '';
  const label = periodLabel(period);
  return `${Number(value).toLocaleString()}${label ? ` ${label}` : ''}`;
}

function formatOfferLocation(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;

  if (value.raw) return value.raw;
  if (value.type === 'US') return 'Nationwide';
  if (value.type === 'GLOBAL') return 'Global';
  if (value.type === 'state' && Array.isArray(value.states) && value.states.length) {
    return `States: ${value.states.join(', ')}`;
  }
  if (value.type === 'zip' && Array.isArray(value.zips) && value.zips.length) {
    return `ZIP codes: ${value.zips.join(', ')}`;
  }
  if (value.type === 'local' && Array.isArray(value.local) && value.local.length) {
    const radii = value.local
      .map((item) => item?.radius)
      .filter((radius) => radius != null && radius !== '');
    if (radii.length) {
      return radii.length === 1
        ? `Within ${radii[0]} miles`
        : radii.map((radius) => `Within ${radius} miles`).join(', ');
    }
    return 'Local service area';
  }

  return value.type || '';
}

function mapOfferToDraft(offer, businessContext, sourceKind) {
  if (!offer || typeof offer !== 'object') return {};

  const unlimitedClaims = offer.unlimitedClaims === true
    || (sourceKind === 'PREVIOUS_OFFER'
      && offer.maxClick == null
      && offer.clickPeriod == null);
  const budget = {
    maxAmount: numberOrUndefined(offer.maxAmount),
    amountPeriod: normalizePeriod(offer.amountPeriod),
    maxClick: unlimitedClaims ? undefined : numberOrUndefined(offer.maxClick),
    clickPeriod: unlimitedClaims ? undefined : normalizePeriod(offer.clickPeriod),
    unlimitedClaims,
  };
  const offerLocation = offer.offerLocation || offer.location || '';
  const businessCategories = Array.isArray(businessContext?.businessCategories)
    ? businessContext.businessCategories
    : [];

  return {
    businessName: offer.businessName || '',
    type: offer.type || '',
    fulfillment: offer.fulfillment || '',
    title: offer.title || '',
    headline1: offer.headline1 || '',
    headline2: offer.headline2 || '',
    description: offer.description || '',
    offerImage: offer.offerImage || '',
    promoCode: offer.promoCode || '',
    redemption: offer.promoCode || '',
    barcode: offer.barcode || '',
    qrCode: offer.qrCode || '',
    siteUrl: offer.siteUrl || '',
    schedule: offer.schedule,
    startDate: offer.startDate || '',
    endDate: offer.endDate || '',
    budget,
    budgetLabel: formatBudget(budget.maxAmount, offer.amountPeriod),
    claims: budget.maxClick != null ? String(budget.maxClick) : '',
    claimsLabel: formatClaims(budget.maxClick, offer.clickPeriod, unlimitedClaims),
    unlimitedClaims,
    keywords: normalizeList(offer.keywords),
    category: businessCategories.map((category) => ({
      categoryId: category.categoryId,
      name: category.name,
      description: category.description,
    })),
    audience: offerLocation,
    offerLocation,
    location: offerLocation,
    locationLabel: formatOfferLocation(offerLocation),
    termsAndConditions: offer.termsAndConditions || '',
    draftOfferId: numberOrUndefined(offer.draftOfferId),
    readyForReview: Boolean(offer.readyForReview),
    complete: Boolean(offer.complete),
  };
}

function mapHistoryMessage(message, index) {
  return {
    id: `${message?.role || 'assistant'}-${message?.timestamp || index}-${index}`,
    role: message?.role === 'user' ? 'user' : 'assistant',
    text: message?.content || '',
    timestamp: message?.timestamp || nowIso(),
  };
}

function mapResponseToMessages(response) {
  const mappedHistory = Array.isArray(response?.history)
    ? response.history.map(mapHistoryMessage).filter((m) => m.text)
    : [];

  const responseMessage = response?.message || '';
  const hasResponseMessage = mappedHistory.some(
    (m) => m.role === 'assistant' && m.text === responseMessage
  );

  if (responseMessage && !hasResponseMessage) {
    mappedHistory.push({
      id: uuid(),
      role: 'assistant',
      text: responseMessage,
      timestamp: nowIso(),
    });
  }

  return mappedHistory.length > 0 ? mappedHistory : [];
}

function extractErrorMessage(error) {
  if (!error) return 'Kaiylee is unavailable right now. Please try again.';
  if (typeof error === 'string') return error;
  if (Array.isArray(error.validationMessages)) return error.validationMessages.join('\n');
  if (Array.isArray(error.violations)) {
    return error.violations
      .map((item) => item?.message || item?.defaultMessage || String(item))
      .filter(Boolean)
      .join('\n');
  }
  if (Array.isArray(error.errors)) {
    return error.errors
      .map((item) => item?.message || item?.defaultMessage || String(item))
      .filter(Boolean)
      .join('\n');
  }
  return error.message
    || error.error
    || error.detail
    || error.agentMessage
    || 'Kaiylee is unavailable right now. Please try again.';
}

function sameMessage(a, b) {
  return (a?.role || 'assistant') === (b?.role || 'assistant')
    && (a?.text || '') === (b?.text || '');
}

function mergeMessages(previousMessages, nextMessages) {
  if (!nextMessages.length) return previousMessages;
  if (!previousMessages.length) return nextMessages;

  const maxOverlap = Math.min(previousMessages.length, nextMessages.length);
  for (let overlap = maxOverlap; overlap > 0; overlap -= 1) {
    const previousTail = previousMessages.slice(previousMessages.length - overlap);
    const nextHead = nextMessages.slice(0, overlap);
    if (previousTail.every((message, index) => sameMessage(message, nextHead[index]))) {
      return [...previousMessages, ...nextMessages.slice(overlap)];
    }
  }

  return [...previousMessages, ...nextMessages];
}

function MessageBubble({ role, text, timestamp }) {
  const isUser = role === 'user';
  return (
    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      {!isUser && (
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '10px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'rgba(249,183,86,0.16)',
            border: '1px solid rgba(249,183,86,0.35)',
            mt: '2px',
            flex: '0 0 auto',
          }}
          aria-hidden="true"
        >
          <SmartToyRoundedIcon sx={{ fontSize: 16, color: '#5A3E00' }} />
        </Box>
      )}

      <Box sx={{ maxWidth: '78%' }}>
        <Paper
          elevation={0}
          sx={{
            px: 1.5,
            py: 1.1,
            borderRadius: 3,
            bgcolor: isUser ? 'rgba(14,117,189,0.12)' : 'rgba(20,20,20,0.04)',
            border: isUser ? '1px solid rgba(14,117,189,0.18)' : '1px solid rgba(20,20,20,0.08)',
            color: '#111',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            lineHeight: 1.35,
          }}
        >
          <Typography variant="body2" sx={{ fontSize: 13.5 }}>
            {text}
          </Typography>
        </Paper>
        {timestamp && (
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.5, color: 'rgba(0,0,0,0.55)', textAlign: isUser ? 'right' : 'left' }}
          >
            {browserDate(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Typography>
        )}
      </Box>

      {isUser && (
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '10px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'rgba(14,117,189,0.12)',
            border: '1px solid rgba(14,117,189,0.18)',
            mt: '2px',
            flex: '0 0 auto',
          }}
          aria-hidden="true"
        >
          <PersonRoundedIcon sx={{ fontSize: 16, color: '#0E75BD' }} />
        </Box>
      )}
    </Box>
  );
}

function TypingIndicator() {
  return (
    <Box
      role="status"
      aria-label="Kaiylee is typing"
      sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', justifyContent: 'flex-start' }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '10px',
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'rgba(249,183,86,0.16)',
          border: '1px solid rgba(249,183,86,0.35)',
          mt: '2px',
          flex: '0 0 auto',
        }}
        aria-hidden="true"
      >
        <SmartToyRoundedIcon sx={{ fontSize: 16, color: '#5A3E00' }} />
      </Box>

      <Box sx={{ maxWidth: '78%' }}>
        <Paper
          elevation={0}
          sx={{
            px: 1.4,
            py: 1.25,
            borderRadius: 3,
            bgcolor: 'rgba(20,20,20,0.04)',
            border: '1px solid rgba(20,20,20,0.08)',
          }}
        >
          <Box aria-hidden="true" sx={{ display: 'flex', alignItems: 'center', gap: '5px', height: 12 }}>
            {[0, 1, 2].map((index) => (
              <Box
                key={index}
                data-testid="kaiylee-typing-dot"
                component="span"
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: 'rgba(0,0,0,0.52)',
                  animation: `${typingDotPulse} 1.15s ease-in-out ${index * 0.16}s infinite`,
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                    opacity: 0.62,
                    transform: 'none',
                  },
                }}
              />
            ))}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

export default function KaiyleeChatPanel({
  open,
  onClose,
  draft,
  onDraftChange,
  onPreviewChange,
  onWorkspaceModeChange,
  businessId,
  onOfferDataRefresh,
  onConfigureOffer,
  variant = 'fixed',
}) {
  const isEmbedded = variant === 'embedded';
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const [suggestedOfferAction, setSuggestedOfferAction] = useState(null);
  const [keywordSuggestions, setKeywordSuggestions] = useState([]);
  const [offerWorkspaceMode, setOfferWorkspaceMode] = useState(null);
  const [canSaveDraft, setCanSaveDraft] = useState(false);
  const [conversationState, setConversationState] = useState('ACTIVE');
  const [errorMessage, setErrorMessage] = useState('');
  const { paused } = useServiceCooldown(KAIYLEE_BUSY);
  const showChatError = (error) => {
    observeServiceAvailability(error);
    setErrorMessage(error?.code === KAIYLEE_BUSY ? '' : extractErrorMessage(error));
  };
  const { organizationId } = useParams()
  const [businessContext, setBusinessContext] = useState({});
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const requestRef = useRef(0);
  const frontendActionTimerRef = useRef(null);
  const contactInteractionRef = useRef(0);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [isInlineHistoryOpen, setIsInlineHistoryOpen] = useState(false);
  const [isFundingOpen, setIsFundingOpen] = useState(false);
  const [contactSession, setContactSession] = useState(null);

  const [messages, setMessages] = useState([]);
  const conversationEnded = conversationState === 'ENDED';
  const replyOptions = quickReplies
    .filter((reply) => (
      draft?.startDate && !draft?.endDate
        ? isAllowedExpirationReply(reply, draft.startDate)
        : String(reply).trim().toLocaleLowerCase() !== 'no end date'
    ))
    .map((reply) => ({ id: reply, label: reply, text: reply }));

  const clearFrontendActionTimer = useCallback(() => {
    if (frontendActionTimerRef.current != null) {
      clearTimeout(frontendActionTimerRef.current);
      frontendActionTimerRef.current = null;
    }
  }, []);

  const openContactForm = () => {
    clearFrontendActionTimer();
    contactInteractionRef.current += 1;
    setContactSession({ businessId, sessionId });
  };

  const applyChatResponse = (response, { replaceMessages = false, allowContactAction = false } = {}) => {
    observeServiceAvailability(response?.availability);
    const responseBusinessContext = response?.businessContext || businessContext;
    setSessionId(response?.sessionId || null);
    setConversationState(response?.conversationState || 'ACTIVE');
    setBusinessContext(responseBusinessContext);
    setQuickReplies(Array.isArray(response?.quickReplies) ? response.quickReplies : []);
    setSuggestedOfferAction(response?.suggestedOfferAction ?? null);
    setKeywordSuggestions(Array.isArray(response?.keywordSuggestions) ? response.keywordSuggestions : []);
    setOfferWorkspaceMode(response?.offerWorkspaceMode ?? null);
    setCanSaveDraft(response?.canSaveDraft === true);
    if (Array.isArray(response?.offerCountRefreshTargets)
      && response.offerCountRefreshTargets.length > 0) {
      onOfferDataRefresh?.(response.offerCountRefreshTargets);
    }
    clearFrontendActionTimer();
    setIsFundingOpen(false);
    const action = response?.frontendAction;
    const openContact = allowContactAction && action?.type === 'OPEN_CONTACT_FORM';
    if ((action?.type === 'OPEN_PAYMENT_WINDOW' || openContact)
      && Number(action.businessId) === Number(businessId)) {
      const requestId = requestRef.current;
      const showWindow = () => {
        if (requestRef.current !== requestId) return;
        if (openContact) {
          setContactSession((current) => current || { businessId, sessionId: response.sessionId });
        } else {
          setIsFundingOpen(true);
        }
      };
      const requestedDelay = Number(action.delayMs);
      const delayMs = Number.isFinite(requestedDelay) && requestedDelay > 0
        ? requestedDelay
        : 0;
      if (delayMs === 0) {
        showWindow();
      } else {
        frontendActionTimerRef.current = setTimeout(() => {
          frontendActionTimerRef.current = null;
          showWindow();
        }, delayMs);
      }
    }
    const responseMessages = mapResponseToMessages(response);
    setMessages((prev) => (
      replaceMessages ? responseMessages : mergeMessages(prev, responseMessages)
    ));
    const draftOffer = mapOfferToDraft(response?.offer, responseBusinessContext);
    onDraftChange?.({
      ...draftOffer,
      budgetEstimate: response?.budgetEstimate ?? null,
      draftOfferId: response?.draftOfferId ?? draftOffer.draftOfferId,
      readyForReview: Boolean(response?.readyForReview ?? draftOffer.readyForReview),
      complete: Boolean(response?.complete ?? draftOffer.complete),
    });
    onWorkspaceModeChange?.(response?.offerWorkspaceMode ?? null);
    const preview = response?.offerPreview;
    onPreviewChange?.(preview?.offer ? {
      ...preview,
      offer: { ...mapOfferToDraft(preview.offer, responseBusinessContext, preview.kind), budgetEstimate: preview.budgetEstimate ?? null },
    } : null);
  };

  const loadHistory = async () => {
    if (businessId == null) return;
    setIsHistoryLoading(true);
    setHistoryError('');
    try {
      const response = await api.get(
        `/chat/sessions?businessId=${encodeURIComponent(businessId)}&page=0&size=10`
      );
      setHistory(normalizeSessionList(response));
    } catch (error) {
      setHistory([]);
      setHistoryError(extractErrorMessage(error));
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const toggleInlineHistory = () => {
    if (!isInlineHistoryOpen) {
      loadHistory();
    }
    setIsInlineHistoryOpen((current) => !current);
  };

  const replaceCurrentChat = async (request, { clearExisting = false } = {}) => {
    clearFrontendActionTimer();
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setIsTyping(true);
    setKeywordSuggestions([]);
    setErrorMessage('');
    setSessionId(null);
    setContactSession(null);
    setConversationState('ACTIVE');
    setQuickReplies([]);
    setSuggestedOfferAction(null);
    setOfferWorkspaceMode(null);
    setCanSaveDraft(false);
    if (clearExisting) {
      onWorkspaceModeChange?.(null);
      onPreviewChange?.(null);
      onDraftChange?.({});
      setMessages([]);
    }

    try {
      const response = await request();
      if (requestRef.current !== requestId) return;
      applyChatResponse(response, { replaceMessages: true });
    } catch (error) {
      if (requestRef.current !== requestId) return;
      showChatError(error);
    } finally {
      if (requestRef.current === requestId) {
        setIsTyping(false);
      }
    }
  };

  const startNewChat = () => replaceCurrentChat(
    () => api.post('/chat/start', buildChatStartPayload(businessId)),
    { clearExisting: true }
  );

  const loadSession = (selectedSessionId) => replaceCurrentChat(
    () => api.get(`/chat/sessions/${encodeURIComponent(selectedSessionId)}`)
  );

  useEffect(() => {
    if (!open || businessId == null) return;
    startNewChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, businessId]);

  useEffect(() => () => {
    requestRef.current += 1;
    clearFrontendActionTimer();
  }, [open, businessId, clearFrontendActionTimer]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => inputRef.current?.focus?.(), 250);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Scroll to bottom smoothly on new messages
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [open, messages.length, isTyping, errorMessage]);

  const send = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || isTyping || conversationEnded || isServicePaused(KAIYLEE_BUSY)) return;

    const activeSessionId = sessionId;
    if (!activeSessionId) {
      setErrorMessage('Kaiylee is still starting. Please try again in a moment.');
      return;
    }

    clearFrontendActionTimer();
    const contactInteraction = contactInteractionRef.current;
    const pendingMessageId = uuid();
    setMessages((prev) => [
      ...prev,
      { id: pendingMessageId, role: 'user', text: trimmed, timestamp: nowIso() },
    ]);

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setIsTyping(true);
    setKeywordSuggestions([]);
    setErrorMessage('');

    try {
      const response = await api.post('/chat', {
        sessionId: activeSessionId,
        message: trimmed,
        timeZone: browserTimeZone(),
      });
      if (requestRef.current !== requestId) return;
      applyChatResponse(response, {
        allowContactAction: contactInteractionRef.current === contactInteraction,
      });
    } catch (error) {
      if (requestRef.current !== requestId) return;
      if (error?.code === KAIYLEE_BUSY) {
        setMessages((previous) => previous.filter((message) => message.id !== pendingMessageId));
        setInput((previous) => previous || trimmed);
      }
      showChatError(error);
    } finally {
      if (requestRef.current === requestId) {
        setIsTyping(false);
      }
    }
  };

  const handlePaymentMethodSaved = async () => {
    if (!sessionId) return;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const savedSessionId = sessionId;
    setIsTyping(true);
    setKeywordSuggestions([]);
    setErrorMessage('');
    try {
      const response = await api.post(
        `/chat/sessions/${encodeURIComponent(savedSessionId)}/resume`,
        {}
      );
      if (requestRef.current !== requestId) return;
      applyChatResponse(response, { replaceMessages: true });
    } catch (error) {
      if (requestRef.current !== requestId) return;
      showChatError(error);
    } finally {
      if (requestRef.current === requestId) {
        setIsTyping(false);
      }
    }
  };

  const resumeConversation = async () => {
    if (!sessionId || isTyping || isServicePaused(KAIYLEE_BUSY)) return;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setIsTyping(true);
    setKeywordSuggestions([]);
    setErrorMessage('');
    try {
      const response = await api.post(
        `/chat/sessions/${encodeURIComponent(sessionId)}/resume`,
        {}
      );
      if (requestRef.current !== requestId) return;
      applyChatResponse(response, { replaceMessages: true });
    } catch (error) {
      if (requestRef.current !== requestId) return;
      showChatError(error);
    } finally {
      if (requestRef.current === requestId) {
        setIsTyping(false);
      }
    }
  };

  const handleSubmit = () => {
    if (isServicePaused(KAIYLEE_BUSY) || isTyping || !sessionId || conversationEnded) return;
    send(input);
    setInput('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const rootSx =
    isEmbedded
      ? {
          height: '100%',
          width: '100%',
        }
      : {
          position: 'fixed',
          left: 0,
          top: HEADER_OFFSET_PX,
          height: `calc(100vh - ${HEADER_OFFSET_PX}px)`,
          width: PANEL_WIDTH,
          transform: open ? 'translateX(0)' : `translateX(-${PANEL_WIDTH + 24}px)`,
          transition: 'transform 260ms cubic-bezier(0.2, 0.8, 0.2, 1)',
          zIndex: 1200,
          px: 1.5,
          pb: 1.5,
          pointerEvents: open ? 'auto' : 'none',
        };

  const paperSx = {
    height: '100%',
    borderRadius: isEmbedded ? 0 : 4,
    border: isEmbedded ? 'none' : '1px solid rgba(0,0,0,0.10)',
    overflow: 'hidden',
    bgcolor: '#fff',
    boxShadow: isEmbedded ? 'none' : '0 18px 60px rgba(0,0,0,0.10)',
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <ImageRecommendationSession sessionKey={`${businessId}:${sessionId}:${open}`}>
    {open && contactSession?.businessId === businessId
      && (contactSession.sessionId == null || contactSession.sessionId === sessionId) && (
      <KaiyleeContactDialog key={`${businessId}:${contactSession.sessionId}`} sessionId={sessionId} onClose={() => setContactSession(null)} />
    )}
    <Box sx={rootSx} role="region" aria-label="Kaiylee assistant chat">
      <Paper
        elevation={0}
        sx={paperSx}
      >
        {/* Header */}
        {!isEmbedded && (
          <Box
          sx={{
            px: 1.75,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background:
              'linear-gradient(135deg, rgba(249,183,86,0.26) 0%, rgba(14,117,189,0.10) 55%, rgba(0,0,0,0.02) 100%)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '12px',
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgba(249,183,86,0.95)',
                border: '1px solid rgba(0,0,0,0.10)',
              }}
              aria-hidden="true"
            >
              <AutoAwesomeRoundedIcon sx={{ fontSize: 18, color: '#fff' }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, lineHeight: 1.05, color: '#111' }}>
                Kaiylee
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.62)' }}>
                Offer creation assistant
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Shows the 10 most recent conversations." arrow>
              <IconButton
                onClick={() => {
                  setIsHistoryOpen(true);
                  loadHistory();
                }}
                size="small"
                aria-label="Open Kaiylee conversation history"
              >
                <HistoryRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="New conversation" arrow>
              <IconButton
                onClick={() => {
                  if (!isServicePaused(KAIYLEE_BUSY)) startNewChat();
                }}
                size="small"
                disabled={paused || isTyping}
                      aria-label="Start a new Kaiylee conversation"
              >
                <AddCommentRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Close" arrow>
              <IconButton onClick={onClose} size="small" aria-label="Close Kaiylee chat">
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        )}

        {isEmbedded && (
          <ClickAwayListener onClickAway={() => setIsInlineHistoryOpen(false)}>
            <Box sx={{ position: 'relative', zIndex: 4 }}>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.875,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid rgba(0,0,0,0.10)',
                  bgcolor: '#fff',
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#222' }}>
                  Chat
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title="Shows the 10 most recent conversations." arrow>
                    <IconButton
                      onClick={toggleInlineHistory}
                      size="small"
                      aria-label="Open Kaiylee conversation history"
                      aria-haspopup="listbox"
                      aria-expanded={isInlineHistoryOpen}
                      aria-controls={isInlineHistoryOpen ? 'kaiylee-inline-history' : undefined}
                      sx={{
                        width: 34,
                        height: 34,
                        color: isInlineHistoryOpen ? '#0e75bd' : 'rgba(0,0,0,0.62)',
                        bgcolor: isInlineHistoryOpen ? 'rgba(14,117,189,0.09)' : 'transparent',
                        '&:hover': { bgcolor: 'rgba(14,117,189,0.08)', color: '#0e75bd' },
                      }}
                    >
                      <HistoryRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="New conversation" arrow>
                    <IconButton
                      onClick={() => {
                        setIsInlineHistoryOpen(false);
                        if (!isServicePaused(KAIYLEE_BUSY)) startNewChat();
                      }}
                      size="small"
                      disabled={paused || isTyping}
                      aria-label="Start a new Kaiylee conversation"
                      sx={{
                        width: 34,
                        height: 34,
                        color: 'rgba(0,0,0,0.62)',
                        '&:hover': { bgcolor: 'rgba(14,117,189,0.08)', color: '#0e75bd' },
                      }}
                    >
                      <AddCommentRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {isInlineHistoryOpen && (
                <Paper
                  id="kaiylee-inline-history"
                  role="listbox"
                  aria-label="Previous Kaiylee conversations"
                  data-testid="kaiylee-inline-history"
                  elevation={8}
                  sx={{
                    position: 'absolute',
                    top: 'calc(100% - 2px)',
                    left: 12,
                    right: 12,
                    maxHeight: 280,
                    overflowY: 'auto',
                    border: '1px solid rgba(0,0,0,0.10)',
                    borderRadius: 2,
                    bgcolor: '#fff',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.16)',
                  }}
                >
                  <Box sx={{ px: 1.5, pt: 1.25, pb: 0.75 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#222' }}>
                      Recent conversations
                    </Typography>
                  </Box>
                  {isHistoryLoading ? (
                    <Box sx={{ minHeight: 72, display: 'grid', placeItems: 'center' }}>
                      <CircularProgress size={24} aria-label="Loading conversation history" />
                    </Box>
                  ) : historyError ? (
                    <Alert severity="error" sx={{ m: 1 }}>
                      {historyError}
                    </Alert>
                  ) : history.length === 0 ? (
                    <Typography variant="body2" sx={{ p: 2, color: 'rgba(0,0,0,0.62)' }}>
                      No previous conversations yet.
                    </Typography>
                  ) : (
                    history.map((item) => (
                      <ListItemButton
                        key={item.sessionId}
                        role="option"
                        aria-selected={item.sessionId === sessionId}
                        selected={item.sessionId === sessionId}
                        onClick={() => {
                          setIsInlineHistoryOpen(false);
                          loadSession(item.sessionId);
                        }}
                        sx={{
                          minHeight: 54,
                          px: 1.5,
                          py: 0.75,
                          borderLeft: '3px solid transparent',
                          borderTop: '1px solid rgba(0,0,0,0.06)',
                          '&.Mui-selected': {
                            bgcolor: 'rgba(14,117,189,0.07)',
                            borderLeftColor: '#0e75bd',
                          },
                          '&.Mui-selected:hover': { bgcolor: 'rgba(14,117,189,0.10)' },
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 700 }} noWrap>
                            {item.title || 'Kaiylee chat'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', color: 'rgba(0,0,0,0.58)' }}
                            noWrap
                          >
                            {sessionStatusLabel(item)} ·{' '}
                            {formatSessionTimestamp(item.updatedAt || item.createdAt)}
                          </Typography>
                        </Box>
                      </ListItemButton>
                    ))
                  )}
                </Paper>
              )}
            </Box>
          </ClickAwayListener>
        )}

        {!isEmbedded && <Divider />}

        {/* Messages */}
        <Box
          ref={listRef}
          sx={{
            px: 1.75,
            py: 1.5,
            flex: 1,
            overflowY: 'auto',
            bgcolor: 'rgba(0,0,0,0.015)',
          }}
        >
          <Stack spacing={1.5}>
            {messages.map((m) => (
              <MessageBubble key={m.id} role={m.role} text={m.text} timestamp={m.timestamp} />
            ))}

            {isTyping && <TypingIndicator />}

            {errorMessage && (
              <Alert severity="error" sx={{ fontSize: 13 }}>
                {errorMessage}
              </Alert>
            )}
          </Stack>
        </Box>

        <Divider />

        {paused && <Box sx={{ px: 1.5, pt: 1.5 }}><ServiceCapacityNotice>{KAIYLEE_BUSY_MESSAGE}</ServiceCapacityNotice></Box>}

        {/* Composer */}
        <Box sx={{ p: 1.5, bgcolor: '#fff' }}>
          {conversationEnded ? (
            <Box
              role="status"
              aria-label="Conversation ended"
              sx={{
                borderRadius: 2.5,
                border: '1px solid rgba(14,117,189,0.16)',
                bgcolor: 'rgba(14,117,189,0.05)',
                p: 1.5,
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 750, color: '#222' }}>
                Conversation ended
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: 'rgba(0,0,0,0.62)' }}>
                Continue where you left off or start a new conversation with Kaiylee.
              </Typography>
              {paused && <Stack direction="row" spacing={1}>
                <Button onClick={() => window.open(businessPath(businessId, organizationId, 'payments'), '_blank', 'noopener,noreferrer')}>Funding & payment</Button>
                <Button onClick={openContactForm}>Contact our team</Button>
              </Stack>}
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={resumeConversation}
                  disabled={paused || isTyping || !sessionId}
                >
                  Resume conversation
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => { if (!isServicePaused(KAIYLEE_BUSY)) startNewChat(); }}
                  disabled={paused || isTyping}
                >
                  Start a new chat
                </Button>
              </Stack>
            </Box>
          ) : (
            <>
              <KaiyleeOfferActions
                key={`${businessId}:${sessionId}`}
                keywordSuggestions={keywordSuggestions}
                businessId={businessId}
                businessContext={businessContext}
                currentImage={draft?.offerImage}
                offer={draft}
                disabled={paused || isTyping || !sessionId || !open}
                suggestedOfferAction={suggestedOfferAction}
                offerWorkspaceMode={offerWorkspaceMode}
                canSaveDraft={canSaveDraft}
                onSend={send}
                onConfigureOffer={onConfigureOffer}
                onContactTeam={openContactForm}
                onOpenFunding={() => window.open(
                  businessPath(businessId, organizationId, 'payments'),
                  '_blank',
                  'noopener,noreferrer'
                )}
              />
              {replyOptions.length > 0 && (
                <Box sx={{ mb: 1.25 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.62)' }}>
                    Quick replies
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                    {replyOptions.map((p) => (
                      <Chip
                        key={p.id}
                        label={p.label}
                        onClick={() => send(p.text)}
                        disabled={paused || isTyping || !sessionId}
                        sx={{
                          bgcolor: '#0e75bd',
                          color: '#fff',
                          border: 0,
                          height: 'auto',
                          maxWidth: '100%',
                          '& .MuiChip-label': {
                            display: 'block',
                            py: 0.75,
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                          },
                          '&:hover': { bgcolor: '#095f9c' },
                          '&:focus-visible': {
                            outline: '3px solid rgba(14,117,189,0.28)',
                            outlineOffset: 2,
                          },
                          '&.Mui-disabled': {
                            bgcolor: 'rgba(0,0,0,0.08)',
                            color: 'rgba(0,0,0,0.38)',
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <StableMultilineTextField
                  disabled={paused}
                  inputRef={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Ask Kaiylee to help craft your offer…"
                  fullWidth
                  minRows={1}
                  maxRows={4}
                  size="small"
                />
                <Tooltip title="Send" arrow>
                  <span>
                    <IconButton
                      onClick={handleSubmit}
                      disabled={paused || !input.trim() || isTyping || !sessionId}
                      aria-label="Send message"
                      sx={{
                        height: 40,
                        width: 40,
                        borderRadius: 2.5,
                        bgcolor: 'rgba(14,117,189,0.12)',
                        color: '#0e75bd',
                        '&:hover': { bgcolor: 'rgba(14,117,189,0.18)' },
                        '&.Mui-disabled': {
                          bgcolor: 'rgba(0,0,0,0.04)',
                          color: 'rgba(0,0,0,0.3)',
                        },
                      }}
                    >
                      <SendRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.75, color: 'rgba(0,0,0,0.55)' }}
              >
                Press Enter to send · Shift+Enter for a new line
              </Typography>
            </>
          )}
        </Box>
      </Paper>

      <PaymentMethodDialog
        open={isFundingOpen}
        onClose={() => setIsFundingOpen(false)}
        onCancel={() => {
          setIsFundingOpen(false);
          send('I closed the payment window');
        }}
        onSuccess={handlePaymentMethodSaved}
        businessId={businessId}
      />

      <Dialog
        open={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        fullWidth
        maxWidth="sm"
        sx={{ zIndex: 1500 }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography component="div" sx={{ fontSize: 20, fontWeight: 750, color: '#111' }}>
            Continue a conversation
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'rgba(0,0,0,0.60)' }}>
            Choose an offer by title. Your latest conversations appear first.
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: 180, p: 1.5 }}>
          {isHistoryLoading ? (
            <Box sx={{ minHeight: 150, display: 'grid', placeItems: 'center' }}>
              <CircularProgress size={28} aria-label="Loading conversation history" />
            </Box>
          ) : historyError ? (
            <Alert severity="error">
              {historyError}
            </Alert>
          ) : history.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
              No conversations yet. Start one with Kaiylee and it will appear here.
            </Typography>
          ) : (
            <List disablePadding sx={{ display: 'grid', gap: 1 }}>
              {history.map((h) => (
                <ListItemButton
                  key={h.sessionId}
                  selected={h.sessionId === sessionId}
                  onClick={() => {
                    setIsHistoryOpen(false);
                    loadSession(h.sessionId);
                  }}
                  sx={{
                    alignItems: 'flex-start',
                    border: '1px solid rgba(14,117,189,0.14)',
                    borderLeft: '4px solid',
                    borderLeftColor: h.sessionId === sessionId ? '#0e75bd' : '#f9b756',
                    borderRadius: 2,
                    px: 1.5,
                    py: 1.25,
                    '&:hover': { bgcolor: 'rgba(14,117,189,0.05)' },
                    '&.Mui-selected': { bgcolor: 'rgba(14,117,189,0.08)' },
                  }}
                >
                  <Box sx={{ width: '100%', minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 14.5,
                        fontWeight: 700,
                        color: '#111',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h.title || 'Kaiylee chat'}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                      sx={{ mt: 0.75 }}
                    >
                      <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.58)' }}>
                        {formatSessionTimestamp(h.updatedAt || h.createdAt)}
                      </Typography>
                      <Chip
                        label={sessionStatusLabel(h)}
                        size="small"
                        sx={{
                          height: 22,
                          flexShrink: 0,
                          bgcolor: h.complete
                            ? 'rgba(46,125,50,0.10)'
                            : 'rgba(249,183,86,0.18)',
                          color: h.complete ? '#2e6f32' : '#7a4b00',
                          fontWeight: 650,
                          fontSize: 11,
                        }}
                      />
                    </Stack>
                  </Box>
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </Box>
    </ImageRecommendationSession>
  );
}

export {
  PANEL_WIDTH,
  HEADER_OFFSET_PX,
  buildChatStartPayload,
  extractErrorMessage,
  isAllowedExpirationReply,
  mapOfferToDraft,
  normalizeList,
  normalizeSessionList,
  sessionStatusLabel,
};
